import React, { ReactElement, useReducer, useMemo, useState } from "react";
import { Some } from "@swiggy-private/js-fp";
import Analytics, { DEFAULT_ANALYTICS_CONTEXT_VALUE } from "@swiggy-private/analytics";

import { GlobalStore } from "@swiggy-private/core-ui";

import {
    UserContext,
    SwgyHeadersKeys,
    CheckoutContext,
    ApiContext,
    ApiContextValue,
    reducer as checkoutReducer,
    CheckoutState,
    ConfigContext,
    ConfigContextValue,
    HardwareBackContext,
    useUserInfo,
    AnalyticsContext,
    VisualEffectContext,
    DeliveryInfoContext,
    GroupOrderContextProvider,
    useCheckoutWebDynamicConfig,
    CheckoutWebDynamicConfigContext,
    PublicApiProvider,
    BackNavigationContextProvider,
    BillToCompanyProvider,
} from "@swiggy-private/checkout-core";
import { useVisualEffects } from "@checkout-main/hooks/useVisualEffects";
import { getAnalyticsConfigParams } from "@swiggy-private/checkout-core";
export interface ContextProviderProps {
    config: {
        apiHost: string;
        paymentHost: string;
        analyticsHost: string;
        paymentReturnUrl?: string;
        addMoneyReturnUrl?: string;
        addSwiggyMoneyReturnUrl?: string;
        cachePaymentData?: boolean;
        simplPostLinkingRedirectUrl: string;
    };
    headers: { [key in Partial<SwgyHeadersKeys>]: string };
    initialState: CheckoutState;
    children: ReactElement;
}

const updateApiContextValue = (apiHost: string, paymentHost: string): ApiContextValue => ({
    host: Some(apiHost),
    paymentHost: Some(paymentHost),
});
const updateConfigContextValue = (config: ContextProviderProps["config"]): ConfigContextValue => ({
    host: Some(config.apiHost),
    analyticsHost: Some(config.analyticsHost),
    paymentReturnUrl: Some(config.paymentReturnUrl),
    addMoneyReturnUrl: Some(config.addMoneyReturnUrl),
    addSwiggyMoneyReturnUrl: Some(config.addSwiggyMoneyReturnUrl),
    cachePaymentData: Some(config.cachePaymentData),
    simplPostLinkingRedirectUrl: Some(config.simplPostLinkingRedirectUrl),
    enableConfirmOrderDifferentialOrderIdAlert: Some(false),
});

export function ContextProvider(props: ContextProviderProps): ReactElement {
    const { config, initialState, children } = props;
    const [user, setUser] = useUserInfo();
    const checkoutWebXpValue = useCheckoutWebDynamicConfig();

    const [checkoutState, setCheckoutState] = useReducer(checkoutReducer, initialState);
    const [hardwareBackState, setHardwareBackState] = useState({ shouldHandle: false });
    const [deliveryContextState, setDeliveryContextState] = useState({ deliveryTimeAnnotation: "", deliveryTime: "" });
    const apiContextValue = useMemo(
        () => updateApiContextValue(config.apiHost, config.paymentHost),
        [config.apiHost, config.paymentHost],
    );
    const configContextValue = useMemo(() => updateConfigContextValue(config), [config]);
    const checkoutContextValue = useMemo(
        () => ({
            checkoutState,
            setCheckoutState,
        }),
        [checkoutState],
    );
    const analyticsContextValue = useMemo(() => {
        const analyticsConfig = getAnalyticsConfigParams(user, config.analyticsHost);
        const analytics = (analyticsConfig && new Analytics(analyticsConfig)) || DEFAULT_ANALYTICS_CONTEXT_VALUE;
        return analytics;
    }, [config.analyticsHost, user]);

    const hardwareBackContextValue = useMemo(() => {
        return {
            state: hardwareBackState,
            setState: setHardwareBackState,
        };
    }, [hardwareBackState]);

    const userContextValue = useMemo(() => {
        return {
            user,
            setUser,
        };
    }, [user, setUser]);

    const deliveryContextValue = useMemo(() => {
        return {
            deliverySelectedSlot: deliveryContextState,
            setDeliverySelectedSlot: setDeliveryContextState,
        };
    }, [deliveryContextState]);

    const visualEffectValue = useVisualEffects();

    // checkout main
    return (
        <AnalyticsContext.Provider value={analyticsContextValue}>
            <CheckoutWebDynamicConfigContext.Provider value={checkoutWebXpValue}>
                <CheckoutContext.Provider value={checkoutContextValue}>
                    <UserContext.Provider value={userContextValue}>
                        <ConfigContext.Provider value={configContextValue}>
                            <HardwareBackContext.Provider value={hardwareBackContextValue}>
                                <VisualEffectContext.Provider value={visualEffectValue}>
                                    <ApiContext.Provider value={apiContextValue}>
                                        <DeliveryInfoContext.Provider value={deliveryContextValue}>
                                            <GroupOrderContextProvider>
                                                <PublicApiProvider>
                                                    <BackNavigationContextProvider>
                                                        <BillToCompanyProvider>
                                                            <GlobalStore>{children}</GlobalStore>
                                                        </BillToCompanyProvider>
                                                    </BackNavigationContextProvider>
                                                </PublicApiProvider>
                                            </GroupOrderContextProvider>
                                        </DeliveryInfoContext.Provider>
                                    </ApiContext.Provider>
                                </VisualEffectContext.Provider>
                            </HardwareBackContext.Provider>
                        </ConfigContext.Provider>
                    </UserContext.Provider>
                </CheckoutContext.Provider>
            </CheckoutWebDynamicConfigContext.Provider>
        </AnalyticsContext.Provider>
    );
}
